import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ClockCounterClockwise, UploadSimple, Trash, PencilSimple, FilePdf,
  Camera, Files, ChatCircleText, Sparkle, PaperPlaneTilt, WarningCircle,
  CheckCircle, CalendarPlus, Receipt, FileMagnifyingGlass, Lightning,
  UserCircle, House,
} from "@phosphor-icons/react";

/**
 * Pestaña "Tracking" del expediente.
 *
 * Lista cronológica (más reciente primero) de TODAS las acciones que se han
 * ejecutado sobre el expediente: subidas/eliminaciones de docs y fotos,
 * ediciones, generaciones de informes, gestiones, incidencias, envío de email,
 * submission al ERP externo, etc.
 *
 * El log se nutre automáticamente de `_log_activity` / `services/activity.py`
 * en cada acción relevante del backend. Frontend sólo presenta.
 */
export default function TrackingTab({ expedienteId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/expedientes/${expedienteId}/activity`, { params: { limit: 500 } })
      .then((r) => { if (!cancelled) setItems(r.data.items || []); })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [expedienteId]);

  if (loading) {
    return <div className="p-6 text-sm text-muted">Cargando tracking…</div>;
  }
  if (!items.length) {
    return (
      <div className="p-12 text-center bg-white rounded-md border border-border" data-testid="tracking-empty">
        <ClockCounterClockwise className="w-10 h-10 mx-auto text-muted" weight="duotone" />
        <div className="mt-2 text-sm font-semibold">Sin actividad registrada</div>
        <div className="text-xs text-muted">Cualquier acción sobre el expediente aparecerá aquí.</div>
      </div>
    );
  }

  // Agrupa por día (es-ES)
  const groups = groupByDay(items);

  return (
    <div className="space-y-6" data-testid="tracking-tab">
      <header>
        <h2 className="font-display font-bold text-base tracking-tight">Tracking del expediente</h2>
        <p className="text-xs text-muted mt-0.5">
          {items.length} {items.length === 1 ? "evento" : "eventos"} registrados.
        </p>
      </header>

      <div className="space-y-5">
        {groups.map((g) => (
          <section key={g.day} data-testid={`tracking-day-${g.day}`}>
            <div className="sticky top-0 z-10 bg-white border-b border-border py-1.5 mb-2 -mx-1 px-1">
              <span className="text-[10px] uppercase tracking-widest font-semibold text-muted">
                {g.label}
              </span>
              <span className="ml-2 text-[10px] text-muted">{g.items.length} eventos</span>
            </div>
            <ol className="space-y-1.5">
              {g.items.map((it) => (
                <TrackingRow key={it.id} item={it} />
              ))}
            </ol>
          </section>
        ))}
      </div>
    </div>
  );
}

/* ====================================================================== */

function TrackingRow({ item }) {
  const meta = describe(item);
  const Icon = meta.icon;
  const userName = item.user?.name || item.user?.email || (item.user_id ? "Usuario" : "Sistema");
  const userCode = item.user?.code;
  const userRole = item.user?.role;
  const time = formatTime(item.timestamp);

  return (
    <li
      className="flex gap-3 items-start py-2 px-2 rounded-md hover:bg-zinc-50"
      data-testid={`tracking-item-${item.id}`}
    >
      <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${meta.bg}`}>
        <Icon className={`w-4 h-4 ${meta.color}`} weight="duotone" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold">{meta.title}</span>
          {meta.tag && (
            <span className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${meta.tagCls || "bg-zinc-100 text-zinc-600"}`}>
              {meta.tag}
            </span>
          )}
          <span className="text-[11px] text-muted">{time}</span>
        </div>
        {meta.subtitle && (
          <div className="text-[12px] text-muted mt-0.5 break-words">{meta.subtitle}</div>
        )}
        <div className="text-[11px] text-muted mt-0.5 inline-flex items-center gap-1">
          <UserCircle className="w-3 h-3" weight="duotone" />
          {userName}{userCode ? ` (${userCode})` : ""}{userRole ? ` · ${userRole}` : ""}
        </div>
      </div>
    </li>
  );
}

/* ====================================================================== */
/* Mapeo de acciones a icono / título / subtítulo                          */
/* ====================================================================== */

function describe(item) {
  const action = item.action || "unknown";
  const d = item.details || {};

  switch (action) {
    case "uploaded":
      return {
        icon: UploadSimple, color: "text-blue-700", bg: "bg-blue-50",
        title: "Parte subido al expediente",
        subtitle: d.filename ? `Archivo: ${d.filename} · ${humanSize(d.size)}` : null,
        tag: "PARTE", tagCls: "bg-blue-100 text-blue-700",
      };
    case "attachment_uploaded":
      return {
        icon: d.kind === "photo" ? Camera : Files,
        color: d.kind === "photo" ? "text-pink-700" : "text-emerald-700",
        bg: d.kind === "photo" ? "bg-pink-50" : "bg-emerald-50",
        title: d.kind === "photo" ? "Foto subida" : "Documento subido",
        subtitle: d.filename ? `${d.filename}${d.folder ? ` · carpeta: ${d.folder}` : ""}${d.size_bytes ? ` · ${humanSize(d.size_bytes)}` : ""}` : null,
        tag: (d.kind || "doc").toUpperCase(),
      };
    case "attachment_deleted":
      return {
        icon: Trash, color: "text-red-700", bg: "bg-red-50",
        title: "Adjunto eliminado",
        subtitle: d.filename ? `${d.filename}${d.kind ? ` (${d.kind})` : ""}` : null,
        tag: "ELIMINADO", tagCls: "bg-red-100 text-red-700",
      };
    case "extracted":
      return {
        icon: Sparkle, color: "text-violet-700", bg: "bg-violet-50",
        title: "Extracción IA completada",
        subtitle: d.fields_count ? `${d.fields_count} campos extraídos` : null,
        tag: "IA", tagCls: "bg-violet-100 text-violet-700",
      };
    case "catastro_auto":
      return {
        icon: House, color: "text-amber-700", bg: "bg-amber-50",
        title: "Catastro consultado automáticamente",
        subtitle: d.referencia ? `Ref: ${d.referencia}` : null,
      };
    case "edited":
      return {
        icon: PencilSimple, color: "text-zinc-700", bg: "bg-zinc-100",
        title: "Datos editados",
        subtitle: Array.isArray(d.fields) && d.fields.length
          ? `Campos: ${d.fields.filter((f) => f !== "updated_at").slice(0, 8).join(", ")}`
          : null,
      };
    case "gestion_added":
      return {
        icon: ChatCircleText, color: "text-blue-700", bg: "bg-blue-50",
        title: "Gestión añadida",
        subtitle: d.text_preview ? `"${d.text_preview}…"` : null,
      };
    case "incidencia_abierta":
    case "ampliacion_abierta":
      return {
        icon: WarningCircle, color: "text-orange-700", bg: "bg-orange-50",
        title: action === "incidencia_abierta" ? "Incidencia abierta" : "Ampliación abierta",
        subtitle: d.pregunta_preview ? `"${d.pregunta_preview}…"` : null,
        tag: "ABIERTA", tagCls: "bg-orange-100 text-orange-700",
      };
    case "incidencia_respondida":
    case "ampliacion_respondida":
      return {
        icon: CheckCircle, color: "text-emerald-700", bg: "bg-emerald-50",
        title: action === "incidencia_respondida" ? "Incidencia respondida" : "Ampliación respondida",
        subtitle: d.respuesta_preview ? `"${d.respuesta_preview}…"` : null,
        tag: "RESPONDIDA", tagCls: "bg-emerald-100 text-emerald-700",
      };
    case "informe_preliminar_generated":
      return {
        icon: FilePdf, color: "text-red-700", bg: "bg-red-50",
        title: "Informe preliminar emitido",
        subtitle: d.filename ? `${d.filename}${d.format ? ` (${d.format})` : ""}` : null,
        tag: "PRELIMINAR", tagCls: "bg-red-100 text-red-700",
      };
    case "informe_final_generated":
      return {
        icon: FilePdf, color: "text-emerald-700", bg: "bg-emerald-50",
        title: "Informe final emitido",
        subtitle: d.filename || null,
        tag: "FINAL", tagCls: "bg-emerald-100 text-emerald-700",
      };
    case "informe_causas_ia_generated":
    case "mobile_notes_developed":
      return {
        icon: Sparkle, color: "text-violet-700", bg: "bg-violet-50",
        title: action === "mobile_notes_developed"
          ? "Notas de campo desarrolladas con IA"
          : "Causas y circunstancias generadas con IA",
        subtitle: d.propuesta_chars ? `${d.propuesta_chars} caracteres` : null,
        tag: "IA", tagCls: "bg-violet-100 text-violet-700",
      };
    case "minuta_requested":
      return {
        icon: Receipt, color: "text-amber-700", bg: "bg-amber-50",
        title: "Minuta solicitada",
        subtitle: d.note || null,
      };
    case "minuta_approved":
      return {
        icon: CheckCircle, color: "text-emerald-700", bg: "bg-emerald-50",
        title: "Minuta aprobada",
        subtitle: d.importe_minuta != null ? `${Number(d.importe_minuta).toLocaleString("es-ES", { minimumFractionDigits: 2 })} €` : null,
      };
    case "dry_run":
      return {
        icon: FileMagnifyingGlass, color: "text-zinc-700", bg: "bg-zinc-100",
        title: "Simulación de envío al ERP",
        tag: "DRY RUN",
      };
    case "submitted":
      return {
        icon: PaperPlaneTilt, color: "text-emerald-700", bg: "bg-emerald-50",
        title: "Enviado al ERP externo",
        subtitle: d.target || null,
        tag: "ENVIADO", tagCls: "bg-emerald-100 text-emerald-700",
      };
    case "error":
      return {
        icon: WarningCircle, color: "text-red-700", bg: "bg-red-50",
        title: "Error",
        subtitle: d.error || d.phase || null,
        tag: (d.phase || "ERROR").toUpperCase(), tagCls: "bg-red-100 text-red-700",
      };
    case "reserva_excede_limite":
      return {
        icon: WarningCircle, color: "text-amber-700", bg: "bg-amber-50",
        title: "Reserva preventiva excede el límite",
        subtitle: d.importe != null ? `${d.importe} €` : null,
        tag: "AVISO", tagCls: "bg-amber-100 text-amber-700",
      };
    case "email_sent":
    case "mail_sent":
      return {
        icon: PaperPlaneTilt, color: "text-blue-700", bg: "bg-blue-50",
        title: "Email enviado",
        subtitle: [d.to, d.subject].filter(Boolean).join(" · ") || null,
      };
    default:
      return {
        icon: Lightning, color: "text-zinc-700", bg: "bg-zinc-100",
        title: humanizeAction(action),
        subtitle: Object.keys(d).length ? JSON.stringify(d).slice(0, 200) : null,
      };
  }
}

/* ====================================================================== */
/* Utilidades                                                              */
/* ====================================================================== */

function humanizeAction(s) {
  return (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function humanSize(b) {
  if (b == null) return "";
  if (b > 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  if (b > 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${b} B`;
}

function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

function groupByDay(items) {
  const map = new Map();
  for (const it of items) {
    const d = (it.timestamp || "").slice(0, 10);
    if (!map.has(d)) map.set(d, []);
    map.get(d).push(it);
  }
  const today = new Date().toISOString().slice(0, 10);
  const yest = (() => { const x = new Date(); x.setDate(x.getDate() - 1); return x.toISOString().slice(0, 10); })();
  return [...map.entries()].map(([day, list]) => {
    let label;
    if (day === today) label = "Hoy";
    else if (day === yest) label = "Ayer";
    else label = formatDayLong(day);
    return { day, label, items: list };
  });
}

function formatDayLong(ymd) {
  try {
    return new Date(ymd).toLocaleDateString("es-ES", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return ymd; }
}
